import type { OwuiFolder } from "@lupp/shared";

export interface FolderNode {
  folder: OwuiFolder;
  children: FolderNode[];
}

/**
 * Builds the nested folder tree from the flat folder list (via parentId).
 * Roots = folders without a (known) parent. Children are sorted by name.
 * Orphans (parent not present) are treated as roots so nothing is lost.
 */
export function buildFolderTree(folders: OwuiFolder[]): FolderNode[] {
  const byId = new Map<string, FolderNode>();
  for (const f of folders) byId.set(f.id, { folder: f, children: [] });

  const roots: FolderNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.folder.parentId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const sortRec = (nodes: FolderNode[]): void => {
    nodes.sort((a, b) => a.folder.name.localeCompare(b.folder.name));
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** All descendant folder ids of `folderId` (excluding itself) — for cycle-safe re-parenting. */
export function descendantIds(folders: OwuiFolder[], folderId: string): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const f of folders) {
    if (!f.parentId) continue;
    const arr = childrenByParent.get(f.parentId);
    if (arr) arr.push(f.id);
    else childrenByParent.set(f.parentId, [f.id]);
  }
  const out = new Set<string>();
  const stack = [...(childrenByParent.get(folderId) ?? [])];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    stack.push(...(childrenByParent.get(id) ?? []));
  }
  return out;
}

/** Returns the path of folders from the root project down to `folderId` (inclusive). */
export function folderPath(folders: OwuiFolder[], folderId: string): OwuiFolder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: OwuiFolder[] = [];
  let cur: OwuiFolder | undefined = byId.get(folderId);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return path;
}
